import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useReducer } from 'react';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { Loader2, Send, Mic, Volume2, ImagePlus, Pencil, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { PageWrapper } from '../components/PageWrapper';
import { ChatLimitSubscribePanel } from '../components/ChatLimitSubscribePanel';
import { JournalAttachmentImage } from '../components/JournalAttachmentImage';
import { CoachJournalDayMessage } from '../components/CoachJournalDayMessage';
import { Textarea } from '../components/ui/textarea';
import {
  getEnabledGoals,
  isAICompanionEnabled,
  getEntries,
  getSettings,
  getProImportSummary,
  getProChatPermissions,
  formatDate,
  getHomeChatState,
  setHomeChatState,
  sanitizeHomeChatMessages,
  homeChatHasAssistantWithText,
  mergeHomeCoachSummaryIntoEntry,
  getAiTokenUsageDay,
  AI_DAILY_TOKEN_BUDGET_DISABLED,
  getVoicePrefs,
  hasCompletedOnboarding,
  saveSettings,
  setHasSeenWebAccessOffer,
  recordProCoachUserMessage,
  touchGoalMentionsFromAssistantReply,
  getDueReminderFollowups,
  markReminderSurfaced,
} from '../utils/storage';
import { applyProChatCommands } from '../utils/chatCommands';
import {
  startSpeechToText,
  speakAloud,
  stopSpeaking,
  isSpeechRecognitionSupported,
  isSpeechSynthesisSupported,
} from '../utils/chatVoice';
import {
  generateWelcomeMessageStream,
  isInvalidAiWelcome,
  summarizeHomeChatForJournalDay,
  generateDailyTasksFromGoals,
} from '../utils/aiCompanion';
import { generateProChatReply } from '../utils/proCoachMLX';
import {
  generateOnboardingChatReply,
  generateOnboardingPostSaveStreamReply,
  buildOnboardingBootstrapUserMessage,
  deriveSignInFirstName,
  parseOnboardingResult,
  mergeDedupedGoalsIntoDraft,
  inferSuggestedNameFromUserReply,
  onboardingGoalDedupeKey,
} from '../utils/onboardingCoach';
import {
  getCurrentFirebaseUser,
  getCurrentFirebaseUid,
  getLinkedProviderIdsLive,
  isClientFirebaseSignedIn,
  linkCurrentUserWithGoogle,
} from '../utils/firebase';
import { pickPhotoBlobFromNativeLibrary, isNativePhotoPicker } from '../utils/journalPhotoPicker';
import { uploadJournalPhotoForDate } from '../utils/journalPhotoCloud';
import {
  formatAiTokenMeterLine,
  getAiTokenDayBudgetState,
  shouldShowAiTokenMeterForUid,
} from '../utils/aiTokenEntitlements';
import { addAiTokenUsageWithSubscriptionNudge } from '../utils/aiSubscriptionNudges';

const coachUid = () => getCurrentFirebaseUid() || '';

/** Persist only fields needed to render journal photos in Home chat (avoid bloating localStorage). */
const slimChatAttachmentForPersist = (att) => {
  if (!att || typeof att !== 'object') return null;
  const storagePath = String(att.storagePath || '').trim();
  const url = String(att.url || '').trim();
  const id = String(att.id || '').trim();
  const caption = String(att.caption || '').trim().slice(0, 500);
  if (!storagePath && !url) return null;
  const out = { storagePath, url, caption };
  if (id) out.id = id;
  return out;
};

const ACROSTIC_FONT_SIZE = 12.6;
const WELCOME_TIMEOUT_MS = 9000;
/** Main coach chat: assistant row usually gets stream deltas quickly (web SSE). */
const CHAT_STUCK_RECOVERY_MS = 30_000;
/**
 * Onboarding (and bootstrap): native uses `onboarding-once` so the assistant bubble stays empty until
 * the whole model reply returns — 30s falsely trips "stuck" while Vertex is still working. Align with
 * `DEFAULT_STREAM_TIMEOUT_MS` (120s) in cloudApi plus a small cushion.
 */
const ONBOARDING_STUCK_RECOVERY_MS = 130_000;
const POST_ONBOARDING_PRO_HANDOFF_USER_HINT =
  '(System: Quick setup in this chat just finished—onboarding only covered name and goals, no open life question yet.) This is their first Home coach turn: sound like a friend checking in on real life, not a product pitch. Ask something light about their day—how it has felt, one thing they are looking forward to, or whether anything nice happened—so they can answer in a line. Do not repeat onboarding framing ("RE-BUILD-ME companion", "thoughtful friend who remembers"), do not ask about goals unless they bring them up, and do not use vague "how can I support you today" style prompts. Match local hour 0–23: hours 0–5 are late night—do not say "good morning". About 40 words or less. Plain text; avoid command brackets unless something truly needs a setting tag.';

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const FrameworkBlock = ({ children, colorVar, delay, compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 12, scale: 0.8 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    style={{
      minHeight: 24,
      height: 24,
      borderRadius: 3,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `var(${colorVar})`,
      padding: compact ? '0 8px' : '0 10px',
      flexShrink: 0,
    }}
  >
    {children}
  </motion.div>
);

const AcrosticRow = ({ word, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className="flex items-center"
  >
    <FrameworkBlock colorVar="--theme-primary" delay={delay}>
      <span className="acrostic-logo-text" style={{ fontSize: ACROSTIC_FONT_SIZE, fontFamily: 'var(--font-heading)', letterSpacing: 0.2 }}>
        <span className="acrostic-logo-text" style={{ fontWeight: 700 }}>{word.charAt(0).toUpperCase()}</span>
        <span className="acrostic-logo-text">{word.slice(1).toLowerCase()}</span>
      </span>
    </FrameworkBlock>
  </motion.div>
);

const LETTER_COLORS = {
  ME: '--theme-primary',
};

const acrosticRows = ['Rebuild', 'Everyday', 'Breathe', 'Understand', 'Improve', 'Learn', 'Do'];

const mkGoalId = (i) => `goal_${i + 1}`;

/** Assistant text returned when the coach API fails or hits an exception path. */
const isChatAiErrorReply = (text) =>
  /Something hiccuped on my side|I hit a snag generating that response|Something went wrong|Please try again|unusually long|got stuck|timed out|Stream read failed|That reply stalled|That got stuck/i.test(
    String(text || '').trim(),
  );

/** Vision captions sometimes open with meta file phrases; strip before the Home follow-up line. */
const stripPhotoCaptionMetaPrefix = (raw) =>
  String(raw || '')
    .replace(/^\s*(photo|image)\s+(?:saved|uploaded)[\s.:!—-]*\s*/i, '')
    .trim();

/** Vision captions sometimes trail with a comma; normalize to one finished sentence. */
const normalizePhotoCaptionSentence = (raw) => {
  let t = stripPhotoCaptionMetaPrefix(raw);
  t = t.replace(/,\s*$/, '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (!/[.!?]$/.test(t)) t = `${t}.`;
  return t;
};

/** Skip embedding bad one-liners in chat (e.g. "This hearty." with no noun). */
const isPhotoCaptionUsableInChat = (normalized) => {
  const t = String(normalized || '').trim();
  if (!t) return false;
  const core = t.replace(/[.!?]+$/, '').trim();
  const lower = core.toLowerCase();
  const words = core.split(/\s+/).filter(Boolean);
  if (words.length < 3) return false;
  if (/^what an?\s+[a-z-]+$/i.test(core)) return false;
  if (
    words.length < 4
    && !/\b(photo|image|meal|dinner|lunch|breakfast|food|curry|rice|plate|table|garden|tree|flower|street|road|car|truck|bike|person|people|family|friend|dog|cat|pet|sky|sunset|beach|mountain|room|kitchen)\b/.test(lower)
  ) {
    return false;
  }
  if (
    words.length === 2
    && /^(this|that|these|those)$/i.test(words[0])
    && /^[a-z]{2,24}$/i.test(words[1])
  ) {
    return false;
  }
  return true;
};

const buildPhotoChatFollowup = (caption) => {
  const t = String(caption || '').trim();
  if (!t) return 'Your photo is saved in today’s journal.';
  const lower = t.toLowerCase();
  let prompt = 'If you want, tell me what was going on there.';
  if (/\b(curry|rice|meal|dinner|lunch|breakfast|food|dish|plate|kitchen)\b/.test(lower)) {
    prompt = 'How was that meal for you?';
  } else if (/\b(car|truck|bike|motorcycle|vehicle|road|drive)\b/.test(lower)) {
    prompt = 'What does this moment with the car mean to you?';
  } else if (/\b(dad|mom|mother|father|family|son|daughter|friend|partner)\b/.test(lower)) {
    prompt = 'How did being with them feel for you?';
  } else if (/\b(dog|cat|pet|puppy|kitten)\b/.test(lower)) {
    prompt = 'What was the best part of that moment with them?';
  }
  return `${t} ${prompt}`;
};

const applyDeleteNeedles = (goals, needles) => {
  let g = [...(goals || [])];
  for (const raw of needles || []) {
    const n = onboardingGoalDedupeKey(raw);
    if (!n || n.length < 2) continue;
    g = g.filter((row) => {
      const k = onboardingGoalDedupeKey(row?.text);
      if (k === n) return false;
      if (n.length >= 4 && (k.includes(n) || n.includes(k))) return false;
      return true;
    });
  }
  return g;
};

export const Home = () => {
  const goals = useMemo(() => getEnabledGoals(), []);
  /** Must read Firebase on each use — auth hydrates after first paint; a mount-time memo stays ''. */
  const resolveSuggestedFirstName = useCallback(() => {
    try {
      return deriveSignInFirstName(getCurrentFirebaseUser()) || '';
    } catch {
      return '';
    }
  }, []);
  const [aiWelcome, setAiWelcome] = useState('');
  const [welcomeStreamText, setWelcomeStreamText] = useState('');
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatPending, setChatPending] = useState(false);
  const [sttListening, setSttListening] = useState(false);
  const sttRef = useRef(null);
  const [voicePrefs, setVoicePrefs] = useState(() => getVoicePrefs());
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [chatPhotoLightbox, setChatPhotoLightbox] = useState(null);
  const [tokenMeterGen, bumpTokenMeter] = useReducer((n) => n + 1, 0);
  const [welcomeFetchFailed, setWelcomeFetchFailed] = useState(false);
  const hydratedRef = useRef(false);
  const persistReadyRef = useRef(false);
  const welcomeFetchInFlightRef = useRef(false);
  /** Prevents duplicate welcome API calls the same calendar day (Strict Mode + handoff races). */
  const welcomeFetchedOkDayRef = useRef('');
  const budgetWarnedRef = useRef(false);
  const chatScrollRef = useRef(null);
  const chatInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const chatScrollRafRef = useRef(null);
  /** Calendar day the in-memory home chat thread belongs to (guards persist after midnight before interval/visibility). */
  const chatDayForThreadRef = useRef('');
  const postHydrateScrollDoneRef = useRef(false);
  /** Synchronous guard so rapid Enter cannot double-send before `chatPending` state commits. */
  const chatSendLockRef = useRef(false);
  const chatStuckRecoveryTimerRef = useRef(null);
  const [editingUserMessageAt, setEditingUserMessageAt] = useState(null);
  const [editingUserDraft, setEditingUserDraft] = useState('');

  const [onboardingAssistantStreaming, setOnboardingAssistantStreaming] = useState(false);
  const [postSavePhase, setPostSavePhase] = useState(null);
  const [draftOnboardName, setDraftOnboardName] = useState('');
  const [draftOnboardGoals, setDraftOnboardGoals] = useState([]);
  const dismissKeyboardAfterSend = useCallback(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    // Hide keyboard after send so user can see/scroll incoming assistant message.
    void Keyboard.hide().catch(() => {});
    requestAnimationFrame(() => {
      const el = chatScrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    });
  }, []);

  const [savingOnboarding, setSavingOnboarding] = useState(false);
  /** Bumped after each assistant reply so chat chrome (e.g. edit) re-renders reliably. */
  const [postAssistantIdleTick, setPostAssistantIdleTick] = useState(0);
  const [onboardingBootToken, setOnboardingBootToken] = useState(0);
  const draftOnboardRef = useRef({ name: '', goals: [] });
  const onboardingSavedRef = useRef(false);
  /** True from first persist through post-save setup stream so a send cannot hit pro chat mid-handoff. */
  const postOnboardingHandoffRef = useRef(false);
  const streamProChatHandoffRef = useRef(() => Promise.resolve());
  const chatMessagesRef = useRef([]);

  const visibleWelcome = String(aiWelcome || '').trim().replace(/\?+$/g, '');
  const welcomeParagraph = String(welcomeStreamText || visibleWelcome || '').trim();
  const settingsDisplayName = getSettings()?.name?.trim() || '';

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  useEffect(() => {
    if (chatStuckRecoveryTimerRef.current != null) {
      clearTimeout(chatStuckRecoveryTimerRef.current);
      chatStuckRecoveryTimerRef.current = null;
    }
    const awaitingAssistant =
      (chatPending || onboardingAssistantStreaming)
      && !savingOnboarding;
    const last = chatMessages[chatMessages.length - 1];
    const emptyAssistant = last?.role === 'assistant' && !String(last.text || '').trim();
    if (!awaitingAssistant || !emptyAssistant) return undefined;

    const stuckMs = !hasCompletedOnboarding() ? ONBOARDING_STUCK_RECOVERY_MS : CHAT_STUCK_RECOVERY_MS;

    chatStuckRecoveryTimerRef.current = window.setTimeout(() => {
      chatStuckRecoveryTimerRef.current = null;
      setChatMessages((prev) => {
        const copy = [...prev];
        const i = copy.length - 1;
        if (i < 0 || copy[i].role !== 'assistant' || String(copy[i].text || '').trim()) return prev;
        copy[i] = {
          ...copy[i],
          text:
            'That reply is taking unusually long—something got stuck. Use Edit on your message to resend, or tap the round arrow to unlock.',
        };
        return copy;
      });
      chatSendLockRef.current = false;
      setChatPending(false);
      setOnboardingAssistantStreaming(false);
      setPostAssistantIdleTick((n) => n + 1);
      toast.error('The reply timed out.');
    }, stuckMs);

    return () => {
      if (chatStuckRecoveryTimerRef.current != null) {
        clearTimeout(chatStuckRecoveryTimerRef.current);
        chatStuckRecoveryTimerRef.current = null;
      }
    };
  }, [chatPending, onboardingAssistantStreaming, chatMessages, savingOnboarding]);

  useEffect(() => {
    if (!chatPhotoLightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setChatPhotoLightbox(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatPhotoLightbox]);

  const welcomeStats = useMemo(() => {
    const entries = getEntries();
    const allEntries = Object.values(entries || {});
    let goalsAchievedCount = 0;

    allEntries.forEach((entry) => {
      const goalStatuses = Object.values(entry?.goals || {});
      goalsAchievedCount += goalStatuses.filter((status) => status?.completed || status?.easyActivity).length;
    });

    return {
      checkInCount: allEntries.length,
      goalsAchievedCount,
    };
  }, []);

  const refreshTokenDisplay = () => {
    bumpTokenMeter();
  };

  const maybeWarnNearDailyLimit = (used) => {
    const cap = getAiTokenDayBudgetState(coachUid()).cap;
    if (!Number.isFinite(cap) || cap <= 0) return;
    const warnAt = Math.floor(cap * 0.95);
    if (used >= warnAt && used < cap && !budgetWarnedRef.current) {
      budgetWarnedRef.current = true;
      toast.message(`You're near today's AI limit (${used.toLocaleString()} / ${cap.toLocaleString()} tokens).`);
    }
  };

  const fetchWelcome = useCallback(async () => {
    if (welcomeFetchInFlightRef.current) return;
    welcomeFetchInFlightRef.current = true;
    try {
      const today = formatDate(new Date());
      if (welcomeFetchedOkDayRef.current === today) {
        const snap = getHomeChatState();
        if (snap.date === today && String(snap.welcomeText || '').trim()) {
          setAiWelcome(snap.welcomeText);
        }
        setLoading(false);
        return;
      }

      const aiEnabled = isAICompanionEnabled();
      if (!aiEnabled) {
        setLoading(false);
        return;
      }

      const usage = getAiTokenUsageDay();
      if (getAiTokenDayBudgetState(coachUid(), usage).overBudget) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setWelcomeFetchFailed(false);
      setWelcomeStreamText('');

      try {
        const result = await Promise.race([
          generateWelcomeMessageStream(
            goals.length > 0 ? goals : [{ text: 'Show a warm welcome' }],
            {
              progressStats: welcomeStats,
              onDelta: (full) => {
                setWelcomeStreamText(full);
              },
              onUsage: (estimate) => {
                if (estimate) {
                  const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), estimate);
                  refreshTokenDisplay();
                  maybeWarnNearDailyLimit(r.used);
                }
              },
            },
          ),
          new Promise((resolve) => window.setTimeout(() => resolve({ success: false, timeout: true }), WELCOME_TIMEOUT_MS)),
        ]);
        setWelcomeStreamText('');
        if (result?.success && result.response) {
          setAiWelcome(result.response);
          welcomeFetchedOkDayRef.current = today;
          setWelcomeFetchFailed(false);
        } else {
          setAiWelcome('');
          setWelcomeFetchFailed(true);
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('Home AI error:', msg, error);
        setAiWelcome('');
        setWelcomeFetchFailed(true);
      } finally {
        setLoading(false);
      }
    } finally {
      welcomeFetchInFlightRef.current = false;
    }
  }, [goals, welcomeStats]);

  const persistOnboardingSettings = useCallback(async (name, rawGoalsList) => {
    const nameTrim = String(name || '').trim() || 'friend';
    const rawGoals = (rawGoalsList || []).filter((g) => g.text?.trim());

    setSavingOnboarding(true);
    try {
      let nextGoals = [];
      if (rawGoals.length > 0) {
        const expanded = await generateDailyTasksFromGoals(rawGoals);
        nextGoals = (expanded && expanded.length > 0 ? expanded : rawGoals).slice(0, 5).map((g, i) => ({
          id: g.id || mkGoalId(i),
          text: String(g.text || '').trim(),
          enabled: true,
          createdAt: g.createdAt || new Date().toISOString(),
          lastMentionedAt: typeof g.lastMentionedAt === 'string' ? g.lastMentionedAt : '',
        }));
      }

      const prev = getSettings() || {};
      const nextSettings = {
        ...prev,
        name: nameTrim.slice(0, 40),
        goals: nextGoals,
        migrated: true,
      };
      delete nextSettings.prompts;
      saveSettings(nextSettings);
      onboardingSavedRef.current = true;
      window.dispatchEvent(new CustomEvent('rebuildme-onboarding-saved'));
    } catch (e) {
      console.error(e);
      toast.error('Could not save setup. Try again.');
      throw e;
    } finally {
      setSavingOnboarding(false);
    }
  }, []);

  const slimOnboardingSession = useCallback(
    (list) =>
      (Array.isArray(list) ? list : [])
        .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && String(m.text || '').trim())
        .map((m) => ({ role: m.role, text: m.text, at: m.at })),
    [],
  );

  const sessionMessagesForOnboarding = useCallback(
    () => slimOnboardingSession(chatMessages),
    [chatMessages, slimOnboardingSession],
  );

  const runOnboardingBootstrap = useCallback(async () => {
    if (hasCompletedOnboarding()) return;
    const usage = getAiTokenUsageDay();
    const onbBudget = getAiTokenDayBudgetState(coachUid(), usage);
    if (onbBudget.overBudget) {
      toast.error(`Daily AI limit reached (${onbBudget.cap.toLocaleString()} tokens).`);
      return;
    }

    const replyId = `asst_onb_${Date.now()}`;
    setOnboardingAssistantStreaming(true);
    setChatMessages([{ role: 'assistant', text: '', at: replyId }]);

    try {
      const signInFirst = resolveSuggestedFirstName();
      const result = await generateOnboardingChatReply({
        sessionMessages: [],
        userMessage: buildOnboardingBootstrapUserMessage(signInFirst),
        onStreamDelta: (cumulative) => {
          setChatMessages((prev) => {
            const copy = [...prev];
            const i = copy.findIndex((m) => m.at === replyId);
            if (i === -1) return prev;
            copy[i] = { ...copy[i], text: cumulative };
            return copy;
          });
        },
      });

      const { patches, cleanedAssistant } = parseOnboardingResult(result);
      const finalText = cleanedAssistant || String(result?.text || '').trim();
      setChatMessages((prev) => {
        const copy = [...prev];
        const i = copy.findIndex((m) => m.at === replyId);
        if (i === -1) return prev;
        copy[i] = { ...copy[i], text: finalText };
        return copy;
      });

      let nextName = patches.name || draftOnboardRef.current.name;
      if (!nextName && signInFirst) nextName = signInFirst;
      let nextGoals = applyDeleteNeedles(draftOnboardRef.current.goals, patches.deleteGoals);
      nextGoals = mergeDedupedGoalsIntoDraft(nextGoals, patches.goals);
      if (nextName) setDraftOnboardName(nextName);
      setDraftOnboardGoals(nextGoals);
      {
        const nm = String(nextName || draftOnboardRef.current.name || '').trim();
        draftOnboardRef.current = { name: nm, goals: nextGoals };
      }

      const est = Number(result?.usageEstimate) || 0;
      if (est > 0) {
        const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), est, undefined, { skipAccounting: true });
        refreshTokenDisplay();
        maybeWarnNearDailyLimit(r.used);
      }
    } catch (err) {
      console.error(err);
      setChatMessages((prev) => {
        const copy = prev.filter((m) => m.at !== replyId);
        return [
          ...copy,
          { role: 'assistant', text: 'Something went wrong. Please try again shortly.', at: new Date().toISOString() },
        ];
      });
    } finally {
      setOnboardingAssistantStreaming(false);
      setPostAssistantIdleTick((n) => n + 1);
    }
  }, [resolveSuggestedFirstName]);

  const streamPostSaveAssistant = useCallback(
    async (variant) => {
      const replyId = `asst_post_${Date.now()}`;
      setOnboardingAssistantStreaming(true);
      setChatMessages((prev) => [...prev, { role: 'assistant', text: '', at: replyId }]);
      try {
        const result = await generateOnboardingPostSaveStreamReply({
          variant,
          sessionMessages: sessionMessagesForOnboarding(),
          onStreamDelta: (cumulative) => {
            setChatMessages((prev) => {
              const copy = [...prev];
              const i = copy.findIndex((m) => m.at === replyId);
              if (i === -1) return prev;
              copy[i] = { ...copy[i], text: cumulative };
              return copy;
            });
          },
        });
        const finalText = String(result?.text || '').trim();
        setChatMessages((prev) => {
          const copy = [...prev];
          const i = copy.findIndex((m) => m.at === replyId);
          if (i === -1) return prev;
          copy[i] = { ...copy[i], text: finalText };
          return copy;
        });
        const est = Number(result?.usageEstimate) || 0;
        if (est > 0) {
          addAiTokenUsageWithSubscriptionNudge(coachUid(), est, undefined, { skipAccounting: true });
          refreshTokenDisplay();
        }
      } catch (e) {
        console.error(e);
        setChatMessages((prev) => {
          const copy = prev.filter((m) => m.at !== replyId);
          return [
            ...copy,
            { role: 'assistant', text: 'Something went wrong. Please try again.', at: new Date().toISOString() },
          ];
        });
      } finally {
        setOnboardingAssistantStreaming(false);
        setPostAssistantIdleTick((n) => n + 1);
      }
    },
    [sessionMessagesForOnboarding],
  );

  const finishPostOnboardingFlow = useCallback(async () => {
    setPostSavePhase(null);
    setHasSeenWebAccessOffer(true);
    try {
      await streamProChatHandoffRef.current();
    } catch (e) {
      console.error('Coach handoff after onboarding:', e);
    }
    try {
      await fetchWelcome();
    } catch {
      /* ignore */
    }
  }, [fetchWelcome]);

  const beginPostSaveFlow = useCallback(async () => {
    setHasSeenWebAccessOffer(true);
    const ids = await getLinkedProviderIdsLive().catch(() => []);
    const hasGoogle = ids.includes('google.com');
    if (!hasGoogle) {
      setPostSavePhase('link_google');
      await streamPostSaveAssistant('google');
      return;
    }
    await finishPostOnboardingFlow();
  }, [streamPostSaveAssistant, finishPostOnboardingFlow]);

  const [calendarTick, setCalendarTick] = useState(0);

  const rollHomeChatIfNewCalendarDay = useCallback(() => {
    const today = formatDate(new Date());
    const saved = getHomeChatState();
    const prevDate = saved.date;
    if (!prevDate || prevDate === today) return false;

    const prevMessages = Array.isArray(saved.messages) ? [...saved.messages] : [];
    const needsOnboardingBoot = !hasCompletedOnboarding();
    budgetWarnedRef.current = false;
    welcomeFetchedOkDayRef.current = '';
    postHydrateScrollDoneRef.current = false;
    chatDayForThreadRef.current = today;
    setChatMessages([]);
    setAiWelcome('');
    setWelcomeStreamText('');
    setHomeChatState({ date: today, messages: [], welcomeText: '' });
    if (prevMessages.length > 0) {
      void summarizeHomeChatForJournalDay({ dateStr: prevDate, messages: prevMessages }).then((sum) => {
        if (sum.success && sum.summary) {
          mergeHomeCoachSummaryIntoEntry(prevDate, sum.summary);
        }
      });
    }
    setLoading(true);
    if (needsOnboardingBoot) {
      queueMicrotask(() => setOnboardingBootToken((t) => t + 1));
    }
    return true;
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setCalendarTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let removed = false;
    let handle;
    try {
      if (!Capacitor.isNativePlatform()) return undefined;
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) setCalendarTick((n) => n + 1);
      }).then((l) => {
        if (removed) {
          try {
            l.remove();
          } catch {
            /* ignore */
          }
          return;
        }
        handle = l;
      });
    } catch {
      /* ignore */
    }
    return () => {
      removed = true;
      try {
        handle?.remove?.();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Restore home chat for today, or roll into a new calendar day (also re-checked every minute + on resume).
  useEffect(() => {
    const today = formatDate(new Date());
    const saved = getHomeChatState();
    const prevDate = saved.date;
    const prevMessages = Array.isArray(saved.messages) ? [...saved.messages] : [];
    const prevWelcome = String(saved.welcomeText || '');

    if (rollHomeChatIfNewCalendarDay()) {
      hydratedRef.current = true;
      return;
    }

    if (prevDate === today) {
      setChatMessages(sanitizeHomeChatMessages(prevMessages));
      setAiWelcome(prevWelcome);
      chatDayForThreadRef.current = prevDate;
      if (prevWelcome) setLoading(false);
    } else {
      setHomeChatState({ date: today, messages: [], welcomeText: '' });
      chatDayForThreadRef.current = today;
    }

    hydratedRef.current = true;

    if (!hasCompletedOnboarding()) {
      const restored = prevDate === today ? sanitizeHomeChatMessages(prevMessages) : [];
      const hasCoach = prevDate === today && homeChatHasAssistantWithText(prevMessages);
      if (restored.length === 0 || !hasCoach) {
        if (prevDate === today && (restored.length > 0 || prevMessages.length > 0)) {
          setChatMessages([]);
          setAiWelcome('');
          setWelcomeStreamText('');
          setHomeChatState({ date: today, messages: [], welcomeText: '' });
        }
        chatDayForThreadRef.current = today;
        setOnboardingBootToken((t) => t + 1);
      }
    }
  }, [calendarTick, rollHomeChatIfNewCalendarDay]);

  useEffect(() => {
    if (!onboardingBootToken) return;
    if (hasCompletedOnboarding()) return;
    if (chatMessages.length > 0) return;
    void runOnboardingBootstrap();
  }, [onboardingBootToken, chatMessages.length, runOnboardingBootstrap]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refreshTokenDisplay();
        setCalendarTick((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    const onEnt = () => bumpTokenMeter();
    window.addEventListener('rebuildme-entitlement-changed', onEnt);
    return () => window.removeEventListener('rebuildme-entitlement-changed', onEnt);
  }, []);

  useEffect(() => {
    if (!hasCompletedOnboarding()) return;
    const due = getDueReminderFollowups(new Date());
    if (!due.length) return;
    const followups = due.map((r) => {
      const action = String(r.text || '').trim() || 'that reminder';
      const timing = r.time ? ` at ${r.time}` : '';
      return {
        id: r.id,
        text: `You asked me to remind you${timing} about "${action}". Did you manage to do it?`,
      };
    });
    setChatMessages((prev) => [
      ...prev,
      ...followups.map((f) => ({ role: 'assistant', text: f.text, at: `asst_rem_${f.id}` })),
    ]);
    followups.forEach((f) => markReminderSurfaced(f.id));
  }, [calendarTick]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!persistReadyRef.current) {
      persistReadyRef.current = true;
      return;
    }
    const today = formatDate(new Date());
    const slated = chatDayForThreadRef.current;
    if (slated && slated !== today) {
      const msgs = [...chatMessages];
      const prevDay = slated;
      budgetWarnedRef.current = false;
      welcomeFetchedOkDayRef.current = '';
      postHydrateScrollDoneRef.current = false;
      chatDayForThreadRef.current = today;
      setChatMessages([]);
      setAiWelcome('');
      setWelcomeStreamText('');
      setHomeChatState({ date: today, messages: [], welcomeText: '' });
      if (msgs.length > 0) {
        void summarizeHomeChatForJournalDay({ dateStr: prevDay, messages: msgs }).then((sum) => {
          if (sum.success && sum.summary) {
            mergeHomeCoachSummaryIntoEntry(prevDay, sum.summary);
          }
        });
      }
      setLoading(true);
      if (!hasCompletedOnboarding()) {
        queueMicrotask(() => setOnboardingBootToken((t) => t + 1));
      }
      return;
    }
    setHomeChatState({
      date: today,
      messages: chatMessages,
      welcomeText: aiWelcome,
    });
  }, [chatMessages, aiWelcome]);

  useLayoutEffect(() => {
    if (!hydratedRef.current) return;
    if (postHydrateScrollDoneRef.current) return;
    if (chatPending || welcomeStreamText || onboardingAssistantStreaming || loading) return;
    const el = chatScrollRef.current;
    if (!el) return;
    postHydrateScrollDoneRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages, aiWelcome, loading, chatPending, welcomeStreamText, onboardingAssistantStreaming]);

  useEffect(() => {
    const aiEnabled = isAICompanionEnabled();
    if (!aiEnabled) {
      setLoading(false);
      return;
    }

    if (!hasCompletedOnboarding()) {
      setLoading(false);
      return;
    }

    const today = formatDate(new Date());
    const saved = getHomeChatState();
    if (saved.date === today && saved.welcomeText) {
      setLoading(false);
      return;
    }

    const usage = getAiTokenUsageDay();
    if (getAiTokenDayBudgetState(coachUid(), usage).overBudget) {
      setLoading(false);
      return;
    }

    void fetchWelcome();
  }, [goals, welcomeStats, fetchWelcome]);

  useEffect(() => {
    const isStreaming = chatPending || Boolean(welcomeStreamText) || onboardingAssistantStreaming;
    if (!isStreaming) return undefined;

    if (chatScrollRafRef.current != null) {
      cancelAnimationFrame(chatScrollRafRef.current);
    }
    chatScrollRafRef.current = requestAnimationFrame(() => {
      chatScrollRafRef.current = null;
      const el = chatScrollRef.current;
      if (!el) return;
      // Only auto-follow while streaming/replying so reading position is preserved otherwise.
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    });
    return () => {
      if (chatScrollRafRef.current != null) {
        cancelAnimationFrame(chatScrollRafRef.current);
        chatScrollRafRef.current = null;
      }
    };
  }, [chatMessages, welcomeStreamText, chatPending, onboardingAssistantStreaming]);

  useEffect(() => {
    const syncVoice = () => setVoicePrefs(getVoicePrefs());
    window.addEventListener('rebuildme-voice-prefs-changed', syncVoice);
    const onVis = () => {
      if (document.visibilityState === 'visible') syncVoice();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('rebuildme-voice-prefs-changed', syncVoice);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => () => {
    try {
      sttRef.current?.stop();
    } catch {
      /* ignore */
    }
    stopSpeaking();
  }, []);

  const streamProChatHandoffAfterOnboarding = useCallback(async () => {
    const usage = getAiTokenUsageDay();
    if (getAiTokenDayBudgetState(coachUid(), usage).overBudget) {
      const nm = String(getSettings()?.name || '').trim().split(/\s+/)[0] || '';
      const fb = nm ? `You are all set. How is your day going today, ${nm}?` : 'You are all set. How is your day going today?';
      setChatMessages((prev) => [...prev, { role: 'assistant', text: fb, at: `asst_handoff_${Date.now()}` }]);
      return;
    }

    if (!isAICompanionEnabled()) {
      const nm = String(getSettings()?.name || '').trim().split(/\s+/)[0] || '';
      const fb = nm ? `Nice to meet you, ${nm}. How is your day going today?` : 'Nice to meet you. How is your day going today?';
      setChatMessages((prev) => [...prev, { role: 'assistant', text: fb, at: `asst_handoff_${Date.now()}` }]);
      return;
    }

    const sessionBase = [...chatMessagesRef.current];
    const replyId = `asst_handoff_${Date.now()}`;
    setChatMessages((prev) => [...prev, { role: 'assistant', text: '', at: replyId }]);
    setChatPending(true);
    try {
      const result = await generateProChatReply({
        userMessage: POST_ONBOARDING_PRO_HANDOFF_USER_HINT,
        importedSummary: getProImportSummary(),
        sessionMessages: sessionBase,
        permissions: getProChatPermissions(),
        onStreamDelta: (cumulative) => {
          setChatMessages((prev) => {
            const copy = [...prev];
            const i = copy.findIndex((m) => m.at === replyId);
            if (i === -1) return prev;
            copy[i] = { ...copy[i], text: cumulative };
            return copy;
          });
        },
      });

      const aiText = result?.text || '';
      setChatMessages((prev) => {
        const copy = [...prev];
        const i = copy.findIndex((m) => m.at === replyId);
        if (i === -1) return prev;
        copy[i] = { role: 'assistant', text: aiText, at: replyId };
        return copy;
      });

      const cmdOutcome = await applyProChatCommands(result?.commands || []);
      cmdOutcome.applied.forEach((msg) => toast.success(msg));
      cmdOutcome.errors.forEach((msg) => toast.error(msg));
      touchGoalMentionsFromAssistantReply(aiText);

      const est = Number(result?.usageEstimate) || 0;
      if (est > 0) {
        const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), est);
        bumpTokenMeter();
        maybeWarnNearDailyLimit(r.used);
        const handoffPost = getAiTokenDayBudgetState(coachUid(), { date: formatDate(new Date()), used: r.used });
        if (handoffPost.overBudget) {
          toast.error(`Daily AI limit reached (${handoffPost.cap.toLocaleString()} tokens). It resets at midnight.`);
        }
      }
    } catch (err) {
      console.error('Post-onboarding handoff:', err);
      const nm = String(getSettings()?.name || '').trim().split(/\s+/)[0] || '';
      const fb = nm ? `How is your day going today, ${nm}?` : 'How is your day going today?';
      setChatMessages((prev) => {
        const copy = prev.filter((m) => m.at !== replyId);
        return [...copy, { role: 'assistant', text: fb, at: new Date().toISOString() }];
      });
    } finally {
      setChatPending(false);
      setPostAssistantIdleTick((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    streamProChatHandoffRef.current = streamProChatHandoffAfterOnboarding;
  }, [streamProChatHandoffAfterOnboarding]);

  const runProCoachForSession = useCallback(async (sessionMessages, replyId, userText) => {
    const result = await generateProChatReply({
      userMessage: userText,
      importedSummary: getProImportSummary(),
      sessionMessages,
      permissions: getProChatPermissions(),
      onStreamDelta: (cumulative) => {
        setChatMessages((prev) => {
          const copy = [...prev];
          const i = copy.findIndex((x) => x.at === replyId);
          if (i === -1) return prev;
          copy[i] = { ...copy[i], text: cumulative };
          return copy;
        });
      },
    });

    const aiText = result?.text || '';
    setChatMessages((prev) => {
      const copy = [...prev];
      const i = copy.findIndex((x) => x.at === replyId);
      if (i === -1) return prev;
      copy[i] = { role: 'assistant', text: aiText, at: replyId };
      return copy;
    });

    const cmdOutcome = await applyProChatCommands(result?.commands || []);
    cmdOutcome.applied.forEach((msg) => toast.success(msg));
    cmdOutcome.errors.forEach((msg) => toast.error(msg));
    touchGoalMentionsFromAssistantReply(aiText);

    const est = Number(result?.usageEstimate) || 0;
    if (est > 0) {
      const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), est);
      refreshTokenDisplay();
      maybeWarnNearDailyLimit(r.used);
      const sessionPost = getAiTokenDayBudgetState(coachUid(), { date: formatDate(new Date()), used: r.used });
      if (sessionPost.overBudget) {
        toast.error(`Daily AI limit reached (${sessionPost.cap.toLocaleString()} tokens). It resets at midnight.`);
      }
    }
  }, []);

  const handleSendMessage = async () => {
    const userText = chatInput.trim();
    if (!userText || chatSendLockRef.current) return;
    if (chatPending || onboardingAssistantStreaming || savingOnboarding || postOnboardingHandoffRef.current) return;

    const todayUsage = getAiTokenUsageDay();
    const sendBudget = getAiTokenDayBudgetState(coachUid(), todayUsage);
    if (sendBudget.overBudget) {
      toast.error(`Daily AI limit reached (${sendBudget.cap.toLocaleString()} tokens). It resets at midnight.`);
      return;
    }

    if (postSavePhase === 'link_google') {
      chatSendLockRef.current = true;
      setChatInput('');
      const userMsg = { role: 'user', text: userText, at: new Date().toISOString() };
      setChatMessages((prev) => [...prev, userMsg]);
      dismissKeyboardAfterSend();
      const yes = /^(y|yes|yeah|yep|sure|ok|please)\b/i.test(userText);
      if (yes) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', text: 'Opening Google sign-in…', at: new Date().toISOString() },
        ]);
        try {
          await linkCurrentUserWithGoogle();
          toast.success('Google connected');
        } catch (e) {
          toast.error(String(e?.message || 'Could not connect Google'));
          setChatMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: 'No problem—you can link Google later in Settings.',
              at: new Date().toISOString(),
            },
          ]);
        }
      } else {
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: 'No problem—you can connect Google anytime in Settings.',
            at: new Date().toISOString(),
          },
        ]);
      }
      window.setTimeout(() => {
        void finishPostOnboardingFlow();
      }, 400);
      chatSendLockRef.current = false;
      return;
    }

    if (!hasCompletedOnboarding()) {
      chatSendLockRef.current = true;
      setChatPending(true);
      const userMsg = { role: 'user', text: userText, at: new Date().toISOString() };
      const nextMessages = [...chatMessages, userMsg];
      const replyId = `asst_${Date.now()}`;
      setChatMessages([...nextMessages, { role: 'assistant', text: '', at: replyId }]);
      setChatInput('');
      dismissKeyboardAfterSend();

      try {
        const result = await generateOnboardingChatReply({
          sessionMessages: slimOnboardingSession(nextMessages),
          userMessage: userText,
          onStreamDelta: (cumulative) => {
            setChatMessages((prev) => {
              const copy = [...prev];
              const i = copy.findIndex((m) => m.at === replyId);
              if (i === -1) return prev;
              copy[i] = { ...copy[i], text: cumulative };
              return copy;
            });
          },
        });

        const { patches, done, cleanedAssistant } = parseOnboardingResult(result);

        const prev = draftOnboardRef.current;
        const signInFirst = resolveSuggestedFirstName();
        let nextName = patches.name || prev.name;
        if (!nextName) {
          const inferred = inferSuggestedNameFromUserReply(userText, signInFirst, patches.name || prev.name);
          if (inferred) nextName = inferred;
        }
        if (!nextName && signInFirst) nextName = signInFirst;

        let nextGoals = applyDeleteNeedles(prev.goals, patches.deleteGoals);
        nextGoals = mergeDedupedGoalsIntoDraft(nextGoals, patches.goals);
        const mergedName = String(nextName || prev.name || signInFirst || '').trim();
        if (mergedName) setDraftOnboardName(mergedName);
        setDraftOnboardGoals(nextGoals);
        draftOnboardRef.current = { name: mergedName, goals: nextGoals };

        const finalText = cleanedAssistant || String(result?.text || '').trim();
        setChatMessages((prev) => {
          const copy = [...prev];
          const i = copy.findIndex((m) => m.at === replyId);
          if (i === -1) return prev;
          copy[i] = { role: 'assistant', text: finalText, at: replyId };
          return copy;
        });

        const est = Number(result?.usageEstimate) || 0;
        if (est > 0) {
          const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), est, undefined, { skipAccounting: true });
          refreshTokenDisplay();
          maybeWarnNearDailyLimit(r.used);
        }

        if (done && mergedName && !onboardingSavedRef.current) {
          postOnboardingHandoffRef.current = true;
          try {
            await persistOnboardingSettings(mergedName, nextGoals);
            await beginPostSaveFlow();
          } catch {
            /* persistOnboardingSettings already toasts */
          } finally {
            postOnboardingHandoffRef.current = false;
          }
        } else if (done && !mergedName) {
          toast.message('Tell me what to call you, then we can wrap up.');
        }
      } catch (err) {
        console.error(err);
        setChatMessages((prev) => {
          const copy = prev.filter((m) => m.at !== replyId);
          return [
            ...copy,
            { role: 'assistant', text: 'Something went wrong. Please try again.', at: new Date().toISOString() },
          ];
        });
      } finally {
        chatSendLockRef.current = false;
        setChatPending(false);
        setPostAssistantIdleTick((n) => n + 1);
      }
      return;
    }

    chatSendLockRef.current = true;
    setChatPending(true);

    const userMsg = { role: 'user', text: userText, at: new Date().toISOString() };
    const nextMessages = [...chatMessages, userMsg];
    const replyId = `asst_${Date.now()}`;
    setChatMessages([...nextMessages, { role: 'assistant', text: '', at: replyId }]);
    setChatInput('');
    dismissKeyboardAfterSend();
    recordProCoachUserMessage();

    try {
      const sessionMessages = nextMessages;
      await runProCoachForSession(sessionMessages, replyId, userText);
    } catch (error) {
      console.error('Home chat error:', error);
      setChatMessages((prev) => {
        const copy = prev.filter((m) => m.at !== replyId);
        return [
          ...copy,
          {
            role: 'assistant',
            text: 'I hit a snag generating that response. Please try again.',
            at: new Date().toISOString(),
          },
        ];
      });
    } finally {
      chatSendLockRef.current = false;
      setChatPending(false);
      setPostAssistantIdleTick((n) => n + 1);
    }
  };

  const handleRecoverStuckChat = useCallback(() => {
    const streamingTail = Boolean(chatPending) || Boolean(onboardingAssistantStreaming);
    const stuck =
      streamingTail
      || Boolean(savingOnboarding)
      || Boolean(postOnboardingHandoffRef.current);
    if (!stuck) return;

    postOnboardingHandoffRef.current = false;
    setSavingOnboarding(false);

    setChatMessages((prev) => {
      const copy = [...prev];
      if (copy.length === 0) return prev;
      const i = copy.length - 1;
      if (copy[i].role !== 'assistant') return prev;
      const t = String(copy[i].text || '').trim();
      if (t && !streamingTail) return prev;
      copy[i] = {
        ...copy[i],
        text:
          'That reply stalled. Edit your message above and resend, or send a shorter line.',
      };
      return copy;
    });
    chatSendLockRef.current = false;
    setChatPending(false);
    setOnboardingAssistantStreaming(false);
    setPostAssistantIdleTick((n) => n + 1);
    toast.message('Chat unlocked—you can edit or resend.');
  }, [chatPending, onboardingAssistantStreaming, savingOnboarding]);

  const showWelcomeBlock = isAICompanionEnabled()
    && hasCompletedOnboarding()
    && chatMessages.length === 0
    && (
      loading
      || welcomeStreamText
      || (visibleWelcome && !isInvalidAiWelcome(aiWelcome))
      || welcomeFetchFailed
    );

  const usageNow = getAiTokenUsageDay();
  const budgetBlocked = getAiTokenDayBudgetState(coachUid(), usageNow).overBudget;

  const handleResendProUserAt = useCallback(
    async (userAt, newText) => {
      const trimmed = String(newText || '').trim();
      if (!trimmed) {
        toast.message('Write something to send.');
        return;
      }
      const usage = getAiTokenUsageDay();
      const resendBudget = getAiTokenDayBudgetState(coachUid(), usage);
      if (resendBudget.overBudget) {
        toast.error(`Daily AI limit reached (${resendBudget.cap.toLocaleString()} tokens). It resets at midnight.`);
        return;
      }
      if (
        chatSendLockRef.current
        || chatPending
        || onboardingAssistantStreaming
        || savingOnboarding
        || postSavePhase
        || postOnboardingHandoffRef.current
      ) {
        return;
      }
      const idx = chatMessages.findIndex((x) => x.at === userAt && x.role === 'user');
      if (idx === -1) return;
      const head = chatMessages.slice(0, idx);
      const userUpdated = { ...chatMessages[idx], text: trimmed, at: chatMessages[idx].at };
      const replyId = `asst_${Date.now()}`;
      setEditingUserMessageAt(null);
      setEditingUserDraft('');
      setChatMessages([...head, userUpdated, { role: 'assistant', text: '', at: replyId }]);
      chatSendLockRef.current = true;
      setChatPending(true);

      if (!hasCompletedOnboarding()) {
        try {
          const result = await generateOnboardingChatReply({
            sessionMessages: slimOnboardingSession([...head, userUpdated]),
            userMessage: trimmed,
            onStreamDelta: (cumulative) => {
              setChatMessages((prev) => {
                const copy = [...prev];
                const i = copy.findIndex((m) => m.at === replyId);
                if (i === -1) return prev;
                copy[i] = { ...copy[i], text: cumulative };
                return copy;
              });
            },
          });

          const { patches, done, cleanedAssistant } = parseOnboardingResult(result);
          const prev = draftOnboardRef.current;
          const signInFirst = resolveSuggestedFirstName();
          let nextName = patches.name || prev.name;
          if (!nextName) {
            const inferred = inferSuggestedNameFromUserReply(trimmed, signInFirst, patches.name || prev.name);
            if (inferred) nextName = inferred;
          }
          if (!nextName && signInFirst) nextName = signInFirst;
          let nextGoals = applyDeleteNeedles(prev.goals, patches.deleteGoals);
          nextGoals = mergeDedupedGoalsIntoDraft(nextGoals, patches.goals);
          const mergedName = String(nextName || prev.name || signInFirst || '').trim();
          if (mergedName) setDraftOnboardName(mergedName);
          setDraftOnboardGoals(nextGoals);
          draftOnboardRef.current = { name: mergedName, goals: nextGoals };

          const finalText = cleanedAssistant || String(result?.text || '').trim();
          setChatMessages((prev) => {
            const copy = [...prev];
            const i = copy.findIndex((m) => m.at === replyId);
            if (i === -1) return prev;
            copy[i] = { role: 'assistant', text: finalText, at: replyId };
            return copy;
          });

          const est = Number(result?.usageEstimate) || 0;
          if (est > 0) {
            const r = addAiTokenUsageWithSubscriptionNudge(coachUid(), est, undefined, { skipAccounting: true });
            refreshTokenDisplay();
            maybeWarnNearDailyLimit(r.used);
          }

          if (done && mergedName && !onboardingSavedRef.current) {
            postOnboardingHandoffRef.current = true;
            try {
              await persistOnboardingSettings(mergedName, nextGoals);
              await beginPostSaveFlow();
            } catch {
              /* persistOnboardingSettings already toasts */
            } finally {
              postOnboardingHandoffRef.current = false;
            }
          } else if (done && !mergedName) {
            toast.message('Tell me what to call you, then we can wrap up.');
          }
        } catch (error) {
          console.error('Onboarding resend error:', error);
          setChatMessages((prev) => {
            const copy = prev.filter((m) => m.at !== replyId);
            return [
              ...copy,
              { role: 'assistant', text: 'Something went wrong. Please try again.', at: new Date().toISOString() },
            ];
          });
        } finally {
          chatSendLockRef.current = false;
          setChatPending(false);
          setPostAssistantIdleTick((n) => n + 1);
        }
        return;
      }

      recordProCoachUserMessage();
      const sessionMessages = [...head, userUpdated];
      try {
        await runProCoachForSession(sessionMessages, replyId, trimmed);
      } catch (error) {
        console.error('Home chat error:', error);
        setChatMessages((prev) => {
          const copy = prev.filter((x) => x.at !== replyId);
          return [
            ...copy,
            {
              role: 'assistant',
              text: 'I hit a snag generating that response. Please try again.',
              at: new Date().toISOString(),
            },
          ];
        });
      } finally {
        chatSendLockRef.current = false;
        setChatPending(false);
        setPostAssistantIdleTick((n) => n + 1);
      }
    },
    [
      chatMessages,
      chatPending,
      onboardingAssistantStreaming,
      savingOnboarding,
      postSavePhase,
      runProCoachForSession,
      slimOnboardingSession,
      resolveSuggestedFirstName,
      persistOnboardingSettings,
      beginPostSaveFlow,
    ],
  );

  const showVoiceInput = Boolean(voicePrefs.voiceInputEnabled) && isSpeechRecognitionSupported();
  const showSpeak = Boolean(voicePrefs.speakEnabled) && isSpeechSynthesisSupported();

  const stopStt = () => {
    try {
      sttRef.current?.stop();
    } catch {
      /* ignore */
    }
    sttRef.current = null;
    setSttListening(false);
  };

  const handleMicToggle = () => {
    if (!showVoiceInput) return;
    if (sttListening) {
      stopStt();
      return;
    }
    if (chatPending || budgetBlocked || onboardingAssistantStreaming || savingOnboarding) return;
    const prefs = getVoicePrefs();
    setSttListening(true);
    sttRef.current = startSpeechToText({
      lang: prefs.sttLang || 'en-US',
      onInterim: () => {},
      onFinal: (t) => {
        stopStt();
        if (t) setChatInput((p) => (p ? `${p.trim()} ${t}` : t).trim());
      },
      onError: (e) => {
        stopStt();
        toast.error(e?.message || 'Voice input failed');
      },
    });
  };

  const handleReadLastReply = async () => {
    if (!showSpeak) return;
    const last = [...chatMessages].reverse().find((m) => m.role === 'assistant' && String(m.text || '').trim());
    const text = String(last?.text || '').trim();
    if (!text) {
      toast.message('No coach message to read yet.');
      return;
    }
    try {
      stopSpeaking();
      await speakAloud(text, getVoicePrefs());
    } catch (e) {
      toast.error(e?.message || 'Could not read aloud');
    }
  };

  const handleHomePhotoFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    // Web: keep this synchronous with the file input gate — awaiting here can reject a valid pick
    // if auth state lags one frame behind `getCurrentFirebaseUser()` (Chrome).
    if (!getCurrentFirebaseUser()?.uid) {
      console.warn('Home photo attach: no Firebase user for upload.');
      toast.message('Sign in to attach photos.');
      return;
    }
    const today = formatDate(new Date());
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const rawBlob = new Blob([await f.arrayBuffer()], { type: f.type || 'image/jpeg' });
      const uploaded = await uploadJournalPhotoForDate(today, rawBlob);
      toast.success('Photo saved to today’s journal');
      const captionRaw = normalizePhotoCaptionSentence(uploaded?.attachment?.caption || '');
      const caption = isPhotoCaptionUsableInChat(captionRaw) ? captionRaw : '';
      const followup = buildPhotoChatFollowup(caption);
      const trimmedAtt = slimChatAttachmentForPersist(uploaded?.attachment);
      const userLine = caption ? `📷 ${caption}` : '📷 Photo added to today’s journal';
      const userAt = new Date().toISOString();
      const assistantAt = new Date().toISOString();
      setChatMessages((prev) => [
        ...prev,
        ...(trimmedAtt
          ? [{ role: 'user', text: userLine, at: userAt, attachment: trimmedAtt }]
          : []),
        { role: 'assistant', text: followup, at: assistantAt },
      ]);
    } catch (err) {
      setPhotoError(String(err?.message || err || 'Photo failed'));
    } finally {
      setPhotoBusy(false);
    }
  };

  /** Native only: web uses a hidden file input + synchronous click from the same handler stack. */
  const handleHomePhotoNativePick = async () => {
    setPhotoError('');
    if (!(await isClientFirebaseSignedIn())) {
      console.warn('Home photo attach: no Firebase session (web currentUser + native check).');
      toast.message('Sign in to attach photos.');
      return;
    }
    setPhotoBusy(true);
    try {
      const rawBlob = await pickPhotoBlobFromNativeLibrary();
      const today = formatDate(new Date());
      const uploaded = await uploadJournalPhotoForDate(today, rawBlob);
      toast.success('Photo saved to today’s journal');
      const captionRaw = normalizePhotoCaptionSentence(uploaded?.attachment?.caption || '');
      const caption = isPhotoCaptionUsableInChat(captionRaw) ? captionRaw : '';
      const followup = buildPhotoChatFollowup(caption);
      const trimmedAtt = slimChatAttachmentForPersist(uploaded?.attachment);
      const userLine = caption ? `📷 ${caption}` : '📷 Photo added to today’s journal';
      const userAt = new Date().toISOString();
      const assistantAt = new Date().toISOString();
      setChatMessages((prev) => [
        ...prev,
        ...(trimmedAtt
          ? [{ role: 'user', text: userLine, at: userAt, attachment: trimmedAtt }]
          : []),
        { role: 'assistant', text: followup, at: assistantAt },
      ]);
    } catch (err) {
      setPhotoError(String(err?.message || err || 'Photo failed'));
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <>
    <PageWrapper
      className="bg-app overflow-hidden"
      contentClassName="flex-1 min-h-0"
      omitBottomNavPadding
    >
      <div className="flex flex-col flex-1 min-h-0 w-full max-w-md mx-auto">
        <div
          ref={chatScrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-6 pt-6"
          style={{ paddingBottom: 'calc(6rem + 5.5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="space-y-5 pb-2">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.12, duration: 0.3 }}
              className="flex justify-center"
            >
              <div className="flex flex-col items-start gap-1.5 w-fit mx-auto">
                {acrosticRows.map((row, idx) => (
                  <AcrosticRow
                    key={row}
                    word={row}
                    delay={0.14 + idx * 0.05}
                  />
                ))}
                <FrameworkBlock
                  colorVar={LETTER_COLORS.ME}
                  delay={0.54}
                  compact
                >
                  <span className="acrostic-logo-text" style={{ fontSize: ACROSTIC_FONT_SIZE, fontWeight: 700, fontFamily: 'var(--font-heading)', letterSpacing: 0.3 }}>
                    ME
                  </span>
                </FrameworkBlock>
              </div>
            </motion.div>

            {goals.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28, duration: 0.3 }}
                className="bg-theme-light rounded-3xl p-5 shadow-ambient border border-theme-secondary"
              >
                <div className="mb-3">
                  <h2 className="font-body text-primary text-sm text-center" style={{ fontWeight: 600 }}>Goals:</h2>
                </div>

                <div className="space-y-3">
                  {goals.map((goal, index) => (
                    <div
                      key={goal.id}
                      className={index > 0 ? 'pt-2' : ''}
                    >
                      <p className="text-primary font-body text-sm mb-1 leading-relaxed">
                        {cap(goal.text)}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {showWelcomeBlock && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.3 }}
              className="mb-5"
            >
              {loading && !welcomeStreamText && (
                <div className="flex items-center gap-2 text-secondary">
                  <Loader2 size={14} className="animate-spin shrink-0" />
                  <span className="font-body text-xs">Refreshing welcome...</span>
                </div>
              )}
              {(welcomeStreamText || (!loading && welcomeParagraph)) && (
                <p className="type-page-title leading-relaxed text-theme-primary">
                  {welcomeStreamText || visibleWelcome}
                </p>
              )}
              {welcomeFetchFailed && !loading && !welcomeStreamText && !welcomeParagraph && (
                <p className="font-body text-sm text-secondary leading-relaxed">
                  {settingsDisplayName
                    ? `I’ll be right back, ${settingsDisplayName}. Try sending another message in a moment.`
                    : 'I’ll be right back. Try sending another message in a moment.'}
                </p>
              )}
            </motion.div>
          )}

          {budgetBlocked ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.3 }}
              className="mb-5 flex justify-center w-full"
            >
              <ChatLimitSubscribePanel uid={coachUid()} />
            </motion.div>
          ) : null}

          <div className="space-y-4 pb-2 w-full max-w-full" data-rebuildme-chat-tick={postAssistantIdleTick}>
            {chatMessages.map((m, idx) => {
              if (m.role === 'user') {
                const canEditUser =
                  postAssistantIdleTick >= 0
                  && !postSavePhase
                  && !chatPending
                  && !onboardingAssistantStreaming
                  && !savingOnboarding;
                const expanded = editingUserMessageAt === m.at;
                const nextM = chatMessages[idx + 1];
                const showQuickResend =
                  canEditUser
                  && nextM?.role === 'assistant'
                  && String(nextM.text || '').trim()
                  && isChatAiErrorReply(nextM.text);
                return (
                  <div key={`${m.at}_${idx}`} className="flex justify-end w-full">
                    <div
                      className={`relative rounded-2xl border border-theme-secondary bg-theme-primary text-primary ${
                        expanded ? 'w-full max-w-full px-3 py-3' : 'max-w-[85%] px-3 py-2'
                      }`}
                    >
                      {!expanded && (
                        <>
                          <p className="font-body text-sm leading-relaxed whitespace-pre-wrap text-left pr-7">
                            {m.text}
                          </p>
                          {m.attachment ? (
                            <button
                              type="button"
                              className="mt-2 block w-full overflow-hidden rounded-xl border border-theme-secondary bg-wisteria-50/20 focus:outline-none focus:ring-2 focus:ring-wisteria-200"
                              onClick={() => setChatPhotoLightbox(m.attachment)}
                              aria-label="View photo full screen"
                            >
                              <JournalAttachmentImage
                                url={m.attachment.url}
                                storagePath={m.attachment.storagePath}
                                alt={String(m.attachment.caption || 'Attached photo').slice(0, 120)}
                                className="max-h-52 w-full object-contain"
                                onUnavailable={() => {}}
                              />
                            </button>
                          ) : null}
                          {canEditUser && (
                            <button
                              type="button"
                              className="absolute bottom-1.5 right-1.5 rounded-md p-1 text-primary/85 hover:bg-black/10 hover:text-primary"
                              aria-label="Edit or resend message"
                              title="Edit"
                              onClick={() => {
                                setEditingUserMessageAt(m.at);
                                setEditingUserDraft(m.text);
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </>
                      )}
                      {expanded && (
                        <div className="flex flex-col gap-2">
                          <Textarea
                            value={editingUserDraft}
                            onChange={(e) => setEditingUserDraft(e.target.value)}
                            rows={4}
                            className="bg-app border-theme-secondary min-h-[88px] text-sm text-primary"
                          />
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              className="font-body text-xs underline text-secondary"
                              onClick={() => {
                                setEditingUserMessageAt(null);
                                setEditingUserDraft('');
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="rounded-lg px-3 py-1.5 font-body text-xs bg-app border border-theme-secondary text-primary"
                              onClick={() => {
                                void handleResendProUserAt(m.at, editingUserDraft);
                              }}
                            >
                              Resend
                            </button>
                          </div>
                        </div>
                      )}
                      {showQuickResend && !expanded && (
                        <div className="mt-2 flex justify-end border-t border-theme-secondary/40 pt-2">
                          <button
                            type="button"
                            className="font-body text-xs text-secondary underline"
                            onClick={() => {
                              void handleResendProUserAt(m.at, m.text);
                            }}
                          >
                            Resend message
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={`${m.at}_${idx}`} className="w-full max-w-full px-1">
                  {!m.text ? (
                    <div className="flex items-center justify-center gap-2 text-secondary py-1">
                      <Loader2 size={14} className="animate-spin shrink-0" />
                      <span className="font-body text-xs">Thinking...</span>
                    </div>
                  ) : (
                    <CoachJournalDayMessage
                      text={m.text}
                      className="type-page-title leading-relaxed text-primary text-center whitespace-pre-wrap"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 px-6 pt-2 pb-[calc(0.45rem+5.5rem+env(safe-area-inset-bottom,0px))] bg-app flex flex-col gap-1">
          <div className="flex items-end gap-2">
            {(showVoiceInput || showSpeak) && (
              <div className="flex flex-col gap-1 shrink-0 pb-0.5">
                {showVoiceInput && (
                  <button
                    type="button"
                    onClick={handleMicToggle}
                    disabled={chatPending || budgetBlocked || onboardingAssistantStreaming || savingOnboarding}
                    className={`rounded-full p-2.5 border border-theme-secondary bg-theme-light text-primary disabled:opacity-50 ${sttListening ? 'ring-2 ring-theme-accent' : ''}`}
                    aria-label={sttListening ? 'Stop listening' : 'Speak to type'}
                    title={sttListening ? 'Stop' : 'Speak to type'}
                  >
                    {sttListening ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
                  </button>
                )}
                {showSpeak && (
                  <button
                    type="button"
                    onClick={() => {
                      void handleReadLastReply();
                    }}
                    disabled={chatPending || budgetBlocked || onboardingAssistantStreaming || savingOnboarding}
                    className="rounded-full p-2.5 border border-theme-secondary bg-theme-light text-primary disabled:opacity-50"
                    aria-label="Read last coach reply aloud"
                    title="Read last reply"
                  >
                    <Volume2 size={18} />
                  </button>
                )}
              </div>
            )}
            <Textarea
              ref={chatInputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Message..."
              rows={3}
              className="bg-theme-light border-theme-secondary min-h-[72px] max-h-[126px] resize-none flex-1"
              disabled={budgetBlocked || onboardingAssistantStreaming || savingOnboarding}
              aria-busy={chatPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (chatPending || budgetBlocked || onboardingAssistantStreaming || savingOnboarding) return;
                  void handleSendMessage();
                }
              }}
            />
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              tabIndex={-1}
              onChange={(e) => {
                void handleHomePhotoFileChange(e);
              }}
              data-testid="home-photo-file-input"
            />
            <div className="flex flex-col items-center gap-1 shrink-0 pb-0.5">
              <button
                type="button"
                onClick={() => {
                  setPhotoError('');
                  if (!isNativePhotoPicker()) {
                    if (!getCurrentFirebaseUid()) {
                      toast.message('Sign in to attach photos.');
                      return;
                    }
                    photoInputRef.current?.click();
                    return;
                  }
                  void handleHomePhotoNativePick();
                }}
                disabled={photoBusy || chatPending || budgetBlocked || onboardingAssistantStreaming || savingOnboarding}
                className="rounded-full p-3 bg-theme-primary text-primary disabled:opacity-50"
                aria-label="Add photo to today’s journal"
                title="Add photo"
                data-testid="home-add-photo"
              >
                {photoBusy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              </button>
              {photoError ? (
                <span className="font-body text-[10px] text-red-600/90 max-w-[4.5rem] text-center leading-tight">
                  {photoError}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  const showRedo =
                    chatPending
                    || onboardingAssistantStreaming
                    || savingOnboarding
                    || postOnboardingHandoffRef.current;
                  if (showRedo) {
                    handleRecoverStuckChat();
                    return;
                  }
                  void handleSendMessage();
                }}
                disabled={
                  budgetBlocked
                  || (!(chatPending
                    || onboardingAssistantStreaming
                    || savingOnboarding
                    || postOnboardingHandoffRef.current)
                    && !chatInput.trim())
                }
                className="rounded-full p-3 bg-theme-primary text-primary disabled:opacity-50"
                aria-label={
                  chatPending
                  || onboardingAssistantStreaming
                  || savingOnboarding
                  || postOnboardingHandoffRef.current
                    ? 'Stop waiting and unlock chat'
                    : 'Send message'
                }
                title={
                  chatPending
                  || onboardingAssistantStreaming
                  || savingOnboarding
                  || postOnboardingHandoffRef.current
                    ? 'Stop waiting and unlock chat'
                    : 'Send'
                }
              >
                {chatPending
                || onboardingAssistantStreaming
                || savingOnboarding
                || postOnboardingHandoffRef.current ? (
                  <RotateCcw size={16} />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </div>
          {isAICompanionEnabled()
            && !AI_DAILY_TOKEN_BUDGET_DISABLED
            && shouldShowAiTokenMeterForUid(coachUid()) && (
            <p key={tokenMeterGen} className="font-body text-sm text-primary leading-snug">
              {formatAiTokenMeterLine(coachUid())}
            </p>
          )}
        </div>
      </div>
    </PageWrapper>
    {chatPhotoLightbox ? (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className="absolute inset-0 z-0 cursor-default border-0 bg-black/70 p-0"
          onClick={() => setChatPhotoLightbox(null)}
          aria-label="Close photo preview"
        />
        <div className="relative z-10 flex w-full max-w-md max-h-[85vh] flex-col items-center gap-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="max-w-full rounded-lg">
            <JournalAttachmentImage
              url={chatPhotoLightbox.url}
              storagePath={chatPhotoLightbox.storagePath}
              alt=""
              className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
              onUnavailable={() => setChatPhotoLightbox(null)}
            />
          </div>
          <button
            type="button"
            className="rounded-2xl px-6 py-3 bg-theme-primary text-primary font-body text-sm border border-theme-secondary shadow-ambient"
            onClick={() => setChatPhotoLightbox(null)}
          >
            Close
          </button>
        </div>
      </div>
    ) : null}
    </>
  );
};

export default Home;
